import { tracked } from '@glimmer/tracking';
import type { IdNameReviewPayload } from 'garaje/models/screener-result';

/**
 * View + review state for the inline "legal name on ID" editor on the
 * id-name-mismatch screener card. Owned by the approval-tiles component (not the
 * model): it lives for the review session and is read on approve to build the
 * `/review` payload.
 */
export default class IdNameReview {
  /** Which inline form is open: closed (`default`), the edit form, or the delete confirmation. */
  @tracked mode: 'default' | 'edit' | 'delete' = 'default';

  /** Whether the reviewer left the legal name untouched, edited it, or removed it. */
  @tracked status: 'untouched' | 'updated' | 'removed' = 'untouched';

  /** Reviewer-edited legal first name. */
  @tracked firstName = '';

  /** Reviewer-edited legal last name. */
  @tracked lastName = '';

  /** True while an inline edit/delete form is open; used to disable approve/deny. */
  get isReviewing(): boolean {
    return this.mode !== 'default';
  }

  /**
   * The legal-name override to send on approve, or null when the name was left
   * untouched. A removed name sends `remove_legal_name` so the BE skips persisting it.
   */
  get payload(): IdNameReviewPayload | null {
    if (this.status === 'updated') {
      return { legal_first_name: this.firstName, legal_last_name: this.lastName };
    }
    if (this.status === 'removed') {
      return { remove_legal_name: true };
    }
    return null;
  }

  reset(): void {
    this.mode = 'default';
    this.status = 'untouched';
    this.firstName = '';
    this.lastName = '';
  }
}
